import {
  ConversationMessage,
  SmartSearchBotResponse,
  ToolCallParameters,
} from '@ltvco/refresh-lib/api';
import { unformatPhone } from '@ltvco/refresh-lib/utils';
import { Record as TeaserRecord } from '@ltvco/refresh-lib/v1';
import { fetchReportByPermalinkInLine } from '@ltvco/refresh-lib/v2';
import OpenAI from 'openai';

import {
  DETECT_IMAGE_REPORTS,
  FUNCTION_CALL_OUTPUT,
  FUNCTION_CALL_TYPE,
  GENERIC_ERROR_MESSAGE,
  MESSAGE,
  NOT_SUPPORTED_SEARCH_TYPE,
  OUTPUT_TEXT,
  REPORT_PROCESSORS,
  SEARCH_TYPES,
  TRIGGER_SEARCH,
  UNSUPPORTED_SEARCH_ERROR_MESSAGE,
  UNSUPPORTED_SEARCH_TYPE_ERROR_MESSAGE,
  CUSTOMER_SERVICE_CONTACT,
  ROLES,
  USE_OPENAI_LOCAL_CONFIGURATION,
  GET_SPECIFIC_REPORT_SECTION,
  NULL_POSSIBLE_REPORT_PARAMS,
} from 'components/ChatBV/constants';
import {
  GenericLLMResponse,
  ProcessedFunctionCallResponse,
  PersonParamsFunctionArguments,
  GenericReportResponse,
  FunctionCallResponse,
  PermalinkOrTeaserOrParamsResult,
  BaseMessage,
  GenericLLMRequest,
  LLMRequestItem,
  InputItem,
  LLMFunctionCallOutput,
  ReponsesAPIRequestProps,
  GetSpecificReportSectionFunctionArguments,
  DetectImageReportsFunctionArguments,
  FlatPersonReport,
  GeneratedReport,
  FlatVehicleReport,
  PossibleReportParams,
  Message,
} from 'components/ChatBV/types';
import {
  getPhoneNumberPermalink,
  getEmailPermalink,
  getPropertyPermalink,
  getVehiclePermalink,
  getPublicRecordsTeaser,
  isUnknown,
  flatReportDataBySearchType,
} from 'components/ChatBV/utils';

import {
  FlatEmailReport,
  FlatPhoneReport,
  FlatPropertyReport,
} from '../types/DeepDiveReportType';

export const mapLLMServiceResponseToGenericLLMResponse = (
  response: SmartSearchBotResponse
): GenericLLMResponse => {
  if (
    Array.isArray(response.data?.tool_calls) &&
    response.data.tool_calls.length > 0
  ) {
    const toolCalls = response.data.tool_calls;
    const functionCalls: FunctionCallResponse[] = toolCalls.map((toolCall) => ({
      call_id: toolCall.call_id,
      name: toolCall.tool || '',
      arguments: toolCall.parameters as
        | GetSpecificReportSectionFunctionArguments
        | ToolCallParameters
        | null,
      type: 'function_call',
      status: 'completed',
    }));

    return {
      context_id: response.context_id,
      data: [null, functionCalls],
      response_id: response.response_id,
    };
  }

  // Extract annotations from response.data if they exist
  const annotations = (response.data as any)?.annotations || [];

  return {
    context_id: response.context_id,
    data: [response.data?.answer ?? '', null],
    response_id: response.response_id,
    sources: annotations,
  };
};

const determineSearchType = (args: ToolCallParameters) => {
  if (args.search_type) {
    return args.search_type;
  }
  if (args.phone_number) {
    return SEARCH_TYPES.PHONE;
  }
  if (args.email) {
    return SEARCH_TYPES.EMAIL;
  }
  if (args.address && args.city && args.state && args.zipcode) {
    return SEARCH_TYPES.PROPERTY;
  }
  if (
    (args.vehicle_year &&
      args.vehicle_make &&
      args.vehicle_model &&
      args.vehicle_trim) ||
    args.vehicle_vin ||
    (args.vehicle_license_plate && args.state)
  ) {
    return SEARCH_TYPES.VEHICLE;
  }
  if (args.first_name && args.last_name) {
    return SEARCH_TYPES.PERSON;
  }
  return NOT_SUPPORTED_SEARCH_TYPE;
};

export const handleFunctionCalls = async (
  functionCalls: FunctionCallResponse[],
  generatedReports: Record<string, GeneratedReport>
): Promise<ProcessedFunctionCallResponse[]> => {
  const processedFunctionCalls: ProcessedFunctionCallResponse[] = [];

  for (const functionCall of functionCalls) {
    if (functionCall.arguments && functionCall.name) {
      if (functionCall.name === TRIGGER_SEARCH) {
        const searchArguments = functionCall.arguments as ToolCallParameters;
        const search_type = determineSearchType(searchArguments);
        const callId = functionCall.call_id;

        if (search_type === NOT_SUPPORTED_SEARCH_TYPE) {
          processedFunctionCalls.push(
            createFunctionCallResultForTriggerSearch(
              functionCall.call_id,
              UNSUPPORTED_SEARCH_TYPE_ERROR_MESSAGE,
              null,
              search_type,
              null
            )
          );
        } else {
          try {
            const searchHandlers = {
              [SEARCH_TYPES.PHONE]: async () => {
                const { phone_number } = searchArguments;
                if (phone_number) {
                  let sanitizedPhoneNumber = unformatPhone(phone_number);
                  if (
                    sanitizedPhoneNumber.length === 11 &&
                    sanitizedPhoneNumber.startsWith('1')
                  ) {
                    sanitizedPhoneNumber = sanitizedPhoneNumber.substring(1);
                  }
                  if (sanitizedPhoneNumber.length !== 10) {
                    return {
                      error: 'Phone number must be 10 digits',
                    };
                  }
                  return await getPhoneNumberPermalink(sanitizedPhoneNumber);
                }
                return {
                  error: 'Phone number is required',
                };
              },
              [SEARCH_TYPES.EMAIL]: async () => {
                const { email } = searchArguments;
                if (email) {
                  return await getEmailPermalink(email);
                }
                return {
                  error: 'Email is required',
                };
              },
              [SEARCH_TYPES.PROPERTY]: async () => {
                const { address, city, state, zipcode } = searchArguments;
                if (
                  !address ||
                  !city ||
                  !state ||
                  !zipcode ||
                  isUnknown(address) ||
                  isUnknown(city) ||
                  isUnknown(state) ||
                  isUnknown(zipcode)
                ) {
                  const missingParams = [];
                  if (!address || isUnknown(address)) {
                    missingParams.push('street address');
                  }
                  if (!city || isUnknown(city)) {
                    missingParams.push('city');
                  }
                  if (!state || isUnknown(state)) {
                    missingParams.push('state');
                  }
                  if (!zipcode || isUnknown(zipcode)) {
                    missingParams.push('zipcode');
                  }
                  return {
                    error: `Missing params: ${missingParams.join(', ')}`,
                  };
                }
                return await getPropertyPermalink(
                  address,
                  city,
                  state,
                  zipcode
                );
              },
              [SEARCH_TYPES.VEHICLE]: async () => {
                const {
                  vehicle_year,
                  vehicle_make,
                  vehicle_model,
                  vehicle_trim,
                  vehicle_vin,
                  vehicle_license_plate,
                  state,
                } = searchArguments;
                return await getVehiclePermalink({
                  state,
                  year: vehicle_year,
                  make: vehicle_make,
                  model: vehicle_model,
                  trim: vehicle_trim,
                  vin: vehicle_vin,
                  licensePlate: vehicle_license_plate,
                });
              },
              [SEARCH_TYPES.PERSON]: async () => {
                const {
                  first_name,
                  last_name,
                  middle_name,
                  age,
                  state,
                  city,
                  title,
                  company,
                  industry,
                  level,
                  birth_month,
                  birth_year,
                  death_month,
                  death_year,
                } = searchArguments;
                if (
                  !first_name ||
                  !last_name ||
                  isUnknown(first_name) ||
                  isUnknown(last_name)
                ) {
                  const missingParams = [];
                  if (!first_name || isUnknown(first_name)) {
                    missingParams.push('first name');
                  }
                  if (!last_name || isUnknown(last_name)) {
                    missingParams.push('last name');
                  }
                  return {
                    error: `Missing params: ${missingParams.join(', ')}`,
                  };
                }
                const teaserResult = await getPublicRecordsTeaser({
                  first_name,
                  last_name,
                  middle_name: middle_name ?? '',
                  age: age ?? '',
                  state: state ?? '',
                  city: city ?? '',
                });
                return {
                  teaser: teaserResult,
                  params: {
                    first_name,
                    last_name,
                    middle_name: middle_name ?? '',
                    age: age ?? '',
                    state: state ?? '',
                    city: city ?? '',
                    title: title ?? '',
                    company: company ?? '',
                    industry: industry ?? '',
                    level: level ?? '',
                    birth_month: birth_month ?? '',
                    birth_year: birth_year ?? '',
                    death_month: death_month ?? '',
                    death_year: death_year ?? '',
                  },
                };
              },
            };

            const handler = searchHandlers[search_type];
            const permalinkOrTeaserOrParamsResult: PermalinkOrTeaserOrParamsResult =
              handler ? await handler() : { permalink: undefined };

            if (permalinkOrTeaserOrParamsResult.permalink) {
              // for all report types that are not person search
              const { data, rawData } = await fetchReportByPermalink(
                permalinkOrTeaserOrParamsResult.permalink || '',
                search_type
              );

              let processedData = data;
              if (
                (search_type === SEARCH_TYPES.PROPERTY ||
                  search_type === SEARCH_TYPES.EMAIL ||
                  search_type === SEARCH_TYPES.VEHICLE) &&
                rawData
              ) {
                const flatReport = flatReportDataBySearchType(
                  rawData,
                  search_type
                ) as
                  | FlatPropertyReport
                  | FlatEmailReport
                  | FlatVehicleReport
                  | undefined;
                processedData = filterSectionsFromFlatReport(flatReport);
              }
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  processedData,
                  permalinkOrTeaserOrParamsResult?.permalink,
                  search_type,
                  rawData as GenericReportResponse
                )
              );
            } else if (permalinkOrTeaserOrParamsResult.teaser) {
              // for person search
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  {
                    amountOfResults:
                      permalinkOrTeaserOrParamsResult.teaser.amountOfResults,
                    city: permalinkOrTeaserOrParamsResult.params?.city || '',
                    state: permalinkOrTeaserOrParamsResult.params?.state || '',
                  },
                  null,
                  search_type,
                  null,
                  permalinkOrTeaserOrParamsResult.teaser.teaserData,
                  permalinkOrTeaserOrParamsResult.params
                    ? permalinkOrTeaserOrParamsResult.params
                    : null
                )
              );
            } else {
              // something went wrong
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  permalinkOrTeaserOrParamsResult.error,
                  null,
                  search_type,
                  null
                )
              );
            }
          } catch (error) {
            // something went wrong with the request in general
            processedFunctionCalls.push(
              createFunctionCallResultForTriggerSearch(
                callId,
                error instanceof Error ? error.message : JSON.stringify(error),
                null,
                search_type,
                null
              )
            );
          }
        }
      }

      if (functionCall.name === CUSTOMER_SERVICE_CONTACT) {
        processedFunctionCalls.push(
          createFunctionCallResultForCustomerServiceContact(
            functionCall.call_id
          )
        );
      }
      if (functionCall.name === GET_SPECIFIC_REPORT_SECTION) {
        const args =
          functionCall.arguments as GetSpecificReportSectionFunctionArguments;

        if (!args) {
          processedFunctionCalls.push(
            createFunctionCallResultForGetSpecificReportSection(
              functionCall.call_id,
              'Invalid function call arguments.',
              undefined,
              undefined,
              undefined
            )
          );
          continue;
        }

        const { permalink } = args;

        // Map API boolean parameters to report section names
        // Note: vehicle_info in API maps to vehicles_info in the report
        const sectionMapping: Record<string, string> = {
          identity_or_owner_identity_info: 'identity_or_owner_identity_info',
          family_info: 'family_info',
          email_phone_or_social_info: 'email_phone_or_social_info',
          trust_and_safety_status_info: 'trust_and_safety_status_info',
          criminal_or_legal_info: 'criminal_or_legal_info',
          property_or_address_info: 'property_or_address_info',
          vehicle_info: 'vehicles_info', // API uses singular, report uses plural
          jobs_education_or_skills_info: 'jobs_education_or_skills_info',
        };

        // Find which boolean parameter is set to true
        let reportSection: string | undefined;
        for (const [apiKey, reportKey] of Object.entries(sectionMapping)) {
          if (
            args[apiKey as keyof GetSpecificReportSectionFunctionArguments] ===
            true
          ) {
            reportSection = reportKey;
            break;
          }
        }

        if (reportSection && permalink && generatedReports[permalink]) {
          const reportToUse = generatedReports[permalink];
          const flatReportData = flatReportDataBySearchType(
            reportToUse.reportData,
            reportToUse.searchType
          );

          if (flatReportData) {
            const specificReportSectionData =
              flatReportData[
                reportSection as keyof (
                  | FlatPhoneReport
                  | FlatPersonReport
                  | FlatPropertyReport
                  | FlatVehicleReport
                  | FlatEmailReport
                )
              ];
            const reportSectionData =
              specificReportSectionData === undefined
                ? 'That information is not available in the report'
                : specificReportSectionData;
            processedFunctionCalls.push(
              createFunctionCallResultForGetSpecificReportSection(
                functionCall.call_id,
                JSON.stringify(reportSectionData),
                reportToUse.reportData,
                reportToUse.permalink,
                reportToUse.searchType
              )
            );
          } else {
            processedFunctionCalls.push(
              createFunctionCallResultForGetSpecificReportSection(
                functionCall.call_id,
                'That information is not available in the report, but you can try with (suggestions: "owners", "property", "businesses", "people", "vehicles")',
                reportToUse.reportData,
                reportToUse.permalink,
                reportToUse.searchType
              )
            );
          }
        } else {
          processedFunctionCalls.push(
            createFunctionCallResultForGetSpecificReportSection(
              functionCall.call_id,
              reportSection
                ? 'That information is not available in the report, but you can try with (suggestions: "owners", "property", "businesses", "people", "vehicles")'
                : 'Please specify exactly one report section to retrieve.',
              undefined,
              undefined,
              undefined
            )
          );
        }
      }

      if (functionCall.name === DETECT_IMAGE_REPORTS) {
        const args = (functionCall.arguments ||
          {}) as DetectImageReportsFunctionArguments;
        const possibleReports = args.possible_reports || [];

        const normalizedReports: PossibleReportParams[] = [];

        for (const report of possibleReports) {
          const hasPersonInfo = report.first_name || report.last_name;
          const hasPhoneInfo = report.phone_number;
          const hasEmailInfo = report.email;
          const hasPropertyInfo =
            report.address || report.city || report.state || report.zipcode;
          const hasVehicleInfo =
            report.vehicle_vin ||
            report.vehicle_license_plate ||
            report.vehicle_make ||
            report.vehicle_model;

          const reportTypesFound: string[] = [];
          if (hasPersonInfo) {
            reportTypesFound.push('person');
          }
          if (hasPhoneInfo) {
            reportTypesFound.push('phone');
          }
          if (hasEmailInfo) {
            reportTypesFound.push('email');
          }
          if (hasPropertyInfo) {
            reportTypesFound.push('property');
          }
          if (hasVehicleInfo) {
            reportTypesFound.push('vehicle');
          }

          if (reportTypesFound.length > 1) {
            if (hasPersonInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'person',
                first_name: report.first_name || '',
                last_name: report.last_name || '',
                middle_name: report.middle_name || '',
                age: report.age || '',
                title: report.title || '',
                company: report.company || '',
                industry: report.industry || '',
                level: report.level || '',
                birth_month: report.birth_month || '',
                birth_year: report.birth_year || '',
                death_month: report.death_month || '',
                death_year: report.death_year || '',
                city: report.city || '',
                state: report.state || '',
              });
            }

            if (hasPhoneInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'phone',
                phone_number: report.phone_number || '',
              });
            }

            if (hasEmailInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'email',
                email: report.email || '',
              });
            }

            if (hasPropertyInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'property',
                address: report.address || '',
                city: report.city || '',
                state: report.state || '',
                zipcode: report.zipcode || '',
              });
            }

            if (hasVehicleInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'vehicle',
                vehicle_vin: report.vehicle_vin || '',
                vehicle_license_plate: report.vehicle_license_plate || '',
                vehicle_year: report.vehicle_year || '',
                vehicle_make: report.vehicle_make || '',
                vehicle_model: report.vehicle_model || '',
                vehicle_trim: report.vehicle_trim || '',
              });
            }
          } else {
            normalizedReports.push({
              ...report,
              search_type: (report.search_type ||
                reportTypesFound[0] ||
                '') as PossibleReportParams['search_type'],
            } as PossibleReportParams);
          }
        }

        processedFunctionCalls.push({
          type: FUNCTION_CALL_OUTPUT,
          call_id: functionCall.call_id,
          output: JSON.stringify({ possible_reports: normalizedReports }),
          possibleReports: normalizedReports,
        });
      }
    }
  }

  return processedFunctionCalls;
};

/**
 * Checks if an object has any properties with content (recursively)
 * Used to verify if nested objects should be included
 */
const checkObjectHasContentInsideSection = (objectData: any): boolean => {
  if (!objectData || typeof objectData !== 'object') {
    return false;
  }

  const keys = Object.keys(objectData);
  for (const key of keys) {
    const objectValue = objectData[key];

    if (objectValue === undefined) {
      continue;
    }

    if (Array.isArray(objectValue)) {
      if (objectValue.length > 0) {
        return true;
      }
      continue;
    }

    if (typeof objectValue === 'object' && objectValue !== null) {
      if (checkObjectHasContentInsideSection(objectValue)) {
        return true;
      }
    } else {
      return true;
    }
  }
  return false;
};

/**
 * Gets the names of sections that have content from flat report
 * Returns permalink with its value, and a formatted message listing sections
 */
const filterSectionsFromFlatReport = (
  flatReport: FlatPropertyReport | FlatEmailReport | undefined
): { permalink?: string; message: string } => {
  if (!flatReport || typeof flatReport !== 'object') {
    return { permalink: undefined, message: '' };
  }

  const sectionsWithContent: string[] = [];
  let permalinkValue: string | undefined;

  const keys = Object.keys(flatReport);

  for (const key of keys) {
    const sectionData = (flatReport as any)[key];

    // Skip if the section is undefined (meaning it has no content)
    if (sectionData === undefined) {
      continue;
    }

    // Include permalink with its value
    if (key === 'permalink') {
      permalinkValue = sectionData;
      continue;
    }

    if (Array.isArray(sectionData)) {
      if (sectionData.length > 0) {
        sectionsWithContent.push(key);
      }
      continue;
    }

    if (typeof sectionData === 'object' && sectionData !== null) {
      const hasContent = checkObjectHasContentInsideSection(sectionData);
      if (hasContent) {
        sectionsWithContent.push(key);
      }
    } else {
      sectionsWithContent.push(key);
    }
  }

  // Build the formatted message
  let message = '';
  if (sectionsWithContent.length > 0) {
    const sectionsList = sectionsWithContent.join(', ');
    message = `System Message: A report summary was displayed to the user. Use this list to suggest follow up questions to the user: ${sectionsList}. DO NOT trigger a get_specific_report_section function call with this message.`;
  }

  return { permalink: permalinkValue, message };
};

const createFunctionCallResultForTriggerSearch = (
  functionCallId: string,
  output: any,
  permalink: string | null,
  searchType: string | null,
  rawData: GenericReportResponse | null,
  teaser: TeaserRecord[] | null = null,
  params: PersonParamsFunctionArguments | null = null
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output: JSON.stringify(output),
  permalink: permalink || undefined,
  searchType: searchType || undefined,
  reportData: rawData || undefined,
  teaser: teaser || undefined,
  params: params || undefined,
});

const createFunctionCallResultForGetSpecificReportSection = (
  functionCallId: string,
  report_section_data: string,
  rawData?: GenericReportResponse,
  permalink?: string,
  searchType?: string
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output: report_section_data,
  permalink,
  searchType,
  reportData: rawData,
});

const createFunctionCallResultForCustomerServiceContact = (
  functionCallId: string
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output:
    'Repeat this specific message: "Please refer to the customer service contact information provided."',
});

export const fetchReportByPermalink = async (
  permalink: string,
  searchType: string
): Promise<{ data: any; rawData: GenericReportResponse | null }> => {
  try {
    const coreResult = await fetchReportByPermalinkInLine(
      permalink,
      searchType
    );
    let processedResult: any = coreResult;

    const reportProcessor =
      REPORT_PROCESSORS[searchType as keyof typeof REPORT_PROCESSORS];

    const processedReportResult = reportProcessor
      ? reportProcessor(coreResult as any, permalink)
      : null;
    if (processedReportResult) {
      processedResult = processedReportResult;
    }
    return { data: processedResult, rawData: coreResult };
  } catch (error) {
    if (error instanceof Error) {
      return { data: error.message, rawData: null };
    }
    return { data: GENERIC_ERROR_MESSAGE, rawData: null };
  }
};

const parseFunctionCallArguments = (
  functionCallArgs: string
): ToolCallParameters | null => {
  try {
    return JSON.parse(functionCallArgs || '{}') as ToolCallParameters;
  } catch (_error) {
    return null;
  }
};

export const mapResponsesAPIResponseToProcessedResponse = (
  response: OpenAI.Responses.Response
): GenericLLMResponse => {
  const contextId = (response as any).context_id || '';

  if (response && response.output && response.output.length > 0) {
    const functionCalls = response.output.filter(
      (output) => output.type === FUNCTION_CALL_TYPE
    );

    if (functionCalls.length > 0) {
      const functionCallsData: FunctionCallResponse[] = functionCalls.map(
        (functionCall) => {
          const functionArgs = parseFunctionCallArguments(
            functionCall.arguments
          );
          return {
            name: functionCall.name ?? '',
            arguments: functionArgs ?? {},
            call_id: functionCall.call_id,
            type: functionCall.type ?? '',
            status: functionCall.status ?? '',
          };
        }
      );
      return {
        context_id: contextId,
        data: [null, functionCallsData],
        response_id: response.id,
      };
    }

    const genericLLMResponse: GenericLLMResponse = {
      context_id: contextId,
      data: [extractOutputText(response.output), null],
      sources: extractOutputAnnotations(response.output),
      response_id: response.id,
    };

    return genericLLMResponse;
  }

  const genericLLMResponse: GenericLLMResponse = {
    context_id: contextId,
    data: [UNSUPPORTED_SEARCH_ERROR_MESSAGE, null],
    response_id: response.id,
  };
  return genericLLMResponse;
};

const findFirstOutputMessage = (output: any[]) => {
  return output.find(
    (output) =>
      output.type === MESSAGE &&
      output.content &&
      output.content.length > 0 &&
      output.content[0]?.type === OUTPUT_TEXT
  );
};

const extractOutputAnnotations = (output: any[]): any[] => {
  if (!output || !Array.isArray(output) || output.length === 0) {
    return [];
  }

  const firstOutputMessage = findFirstOutputMessage(output);
  if (
    !firstOutputMessage ||
    !firstOutputMessage.content ||
    !Array.isArray(firstOutputMessage.content) ||
    firstOutputMessage.content.length === 0
  ) {
    return [];
  }

  const firstContent = firstOutputMessage.content[0];
  if (!firstContent || !firstContent.annotations) {
    return [];
  }

  return Array.isArray(firstContent.annotations)
    ? firstContent.annotations
    : [];
};

export const extractOutputText = (
  output: any[],
  fallbackMessage: string = GENERIC_ERROR_MESSAGE
): string => {
  if (!output || output.length === 0) {
    return fallbackMessage;
  }

  const firstOutputMessage = findFirstOutputMessage(output);
  if (firstOutputMessage) {
    return firstOutputMessage.content[0].text || fallbackMessage;
  }

  return fallbackMessage;
};

export const buildUserRequest = (
  message: BaseMessage,
  lastContextId?: string,
  useWebSearch?: boolean
): GenericLLMRequest => ({
  items: [
    {
      type: null,
      call_id: null,
      output: null,
      role: ROLES.USER,
      content: message.content,
      metadata: null,
    },
  ],
  context_id: lastContextId || null,
  useWebSearch,
});

export const buildLLMOrientedRequest = (
  request: GenericLLMRequest
): ReponsesAPIRequestProps | LLMRequestItem => {
  if (USE_OPENAI_LOCAL_CONFIGURATION) {
    const itemsToSend: InputItem[] = [];

    for (const item of request.items) {
      if (item.type === FUNCTION_CALL_OUTPUT && item.call_id && item.output) {
        itemsToSend.push({
          type: item.type,
          call_id: item.call_id,
          output: item.output,
        });
      } else {
        itemsToSend.push({
          role: ROLES.USER,
          content: item.content || '',
        });
      }
    }
    return {
      input: itemsToSend,
      previous_response_id: request.context_id || undefined,
    };
  }
  // If not using local configuration, we need to build the request for the LLM

  const functionCallOutputs: LLMFunctionCallOutput[] = [];
  const prompt: string[] = [];

  for (const item of request.items) {
    if (item.type === FUNCTION_CALL_OUTPUT && item.call_id && item.output) {
      const functionCallOutput: LLMFunctionCallOutput = {
        id: item.call_id,
        content: item.output,
        metadata: item.metadata || '',
      };
      functionCallOutputs.push(functionCallOutput);
    } else {
      prompt.push(item.content || '');
    }
  }

  const requestToSend: LLMRequestItem = {
    ...(prompt.length > 0 && { prompt: prompt.toString() }),
    ...(functionCallOutputs.length > 0 && {
      function_call_outputs: functionCallOutputs,
    }),
    contextId: request.context_id || undefined,
  };

  return requestToSend;
};

// Helper function to extract attached-data from message content
export const extractAttachedData = (
  content: string
): {
  cleanContent: string;
  attachedData: { permalink?: string; reportType?: string } | null;
} => {
  // Match [attached-data: {permalink: abc123, report-Type: person}] or similar variations
  const attachedDataRegex = /\[attached-data:\s*\{([^}]+)\}\]/i;
  const match = content.match(attachedDataRegex);

  if (!match) {
    return { cleanContent: content, attachedData: null };
  }

  const cleanContent = content.replace(attachedDataRegex, '').trim();
  const attachedDataString = match[1];

  // Parse attached-data: {permalink: abc123, report-Type: person}
  // Handle both "report-Type" and "reportType" variations
  const permalinkMatch = attachedDataString.match(/permalink:\s*([^\s,}]+)/i);
  const reportTypeMatch = attachedDataString.match(
    /report-?Type:\s*([^\s,}]+)/i
  );

  const attachedData: { permalink?: string; reportType?: string } = {};
  if (permalinkMatch) {
    attachedData.permalink = permalinkMatch[1].trim();
  }
  if (reportTypeMatch) {
    attachedData.reportType = reportTypeMatch[1].trim();
  }

  return {
    cleanContent,
    attachedData: Object.keys(attachedData).length > 0 ? attachedData : null,
  };
};

export const mapConversationMessagesToMessages = (
  messages: ConversationMessage[],
  generatedReports?: Record<string, GeneratedReport>
): Message[] => {
  let savedMetadata: string | null = null;
  const resultMessages: Message[] = [];

  messages.forEach((message) => {
    // Check if this message has function_call_outputs
    // This can happen in both USER and ASSISTANT messages
    if (
      message.function_call_outputs &&
      message.function_call_outputs.length > 0
    ) {
      // Look for metadata in any of the function_call_outputs
      for (const output of message.function_call_outputs) {
        if (output.metadata) {
          savedMetadata = output.metadata;
          break; // Use the first metadata found
        }
      }

      // For USER messages with function_call_outputs, try to extract report data
      // This is for phone, property, email, vehicle reports
      if (message.role === ROLES.USER) {
        for (const output of message.function_call_outputs) {
          if (output.content) {
            try {
              const parsedContent = JSON.parse(output.content);
              if (parsedContent.permalink && parsedContent.data) {
                // Extract search type from the data or try to determine it
                // For phone reports, we can check if there's phoneNumber in data
                let searchType: string | undefined;

                if (parsedContent.data.phoneNumber) {
                  searchType = SEARCH_TYPES.PHONE;
                } else if (parsedContent.data.email) {
                  searchType = SEARCH_TYPES.EMAIL;
                } else if (
                  parsedContent.data.address ||
                  parsedContent.data.propertyAddress
                ) {
                  searchType = SEARCH_TYPES.PROPERTY;
                } else if (
                  parsedContent.data.vin ||
                  parsedContent.data.vehicleVin
                ) {
                  searchType = SEARCH_TYPES.VEHICLE;
                }

                if (searchType && parsedContent.permalink) {
                  // Check if report exists in generatedReports
                  const existingReport =
                    generatedReports?.[parsedContent.permalink];

                  // Create assistant message with report or loading state
                  const assistantMessage: Message = existingReport
                    ? {
                        content: '',
                        role: ROLES.ASSISTANT,
                        reports: [
                          {
                            reportData: existingReport.reportData,
                            type: existingReport.searchType,
                            permalink: existingReport.permalink,
                          },
                        ],
                        executedFunctionCall: TRIGGER_SEARCH,
                      }
                    : {
                        content: '',
                        role: ROLES.ASSISTANT,
                        permalink: parsedContent.permalink,
                        searchType,
                        isLoadingReport: true,
                        executedFunctionCall: TRIGGER_SEARCH,
                      };

                  resultMessages.push(assistantMessage);
                  // Don't add user message, it's internal
                  return;
                }
              }
            } catch {
              // Not valid JSON, continue
            }
          }
        }
      }

      // This message has function calls, so it will be mapped to null
      // The metadata will be applied to the next assistant message without function calls
      const mappedMessage = mapConversationMessageToMessage(message, null);
      if (mappedMessage) {
        resultMessages.push(mappedMessage);
      }
      return;
    }

    // Map the message, applying saved metadata if this is an assistant message without function calls
    const mappedMessage = mapConversationMessageToMessage(
      message,
      savedMetadata
    );

    // Process attached-data for user messages (only for person, obituary, and teaser-derived types)
    if (mappedMessage && mappedMessage.role === ROLES.USER) {
      const { cleanContent, attachedData } = extractAttachedData(
        mappedMessage.content
      );

      if (attachedData?.permalink && attachedData?.reportType) {
        // Only process attached-data for person, obituary, and teaser-derived types
        // For phone, property, email, vehicle - just clean the content but don't create assistant message
        const reportType = attachedData.reportType.toLowerCase();
        const shouldProcessAttachedData =
          reportType === SEARCH_TYPES.PERSON ||
          reportType === SEARCH_TYPES.OBITUARY;

        // Always update user message with clean content
        mappedMessage.content = cleanContent;

        if (shouldProcessAttachedData) {
          // Check if report exists in generatedReports
          const existingReport = generatedReports?.[attachedData.permalink];

          // Create assistant message with report or loading state
          const assistantMessage: Message = existingReport
            ? {
                content: '',
                role: ROLES.ASSISTANT,
                reports: [
                  {
                    reportData: existingReport.reportData,
                    type: existingReport.searchType,
                    permalink: existingReport.permalink,
                  },
                ],
                executedFunctionCall: TRIGGER_SEARCH,
              }
            : {
                content: '',
                role: ROLES.ASSISTANT,
                permalink: attachedData.permalink,
                searchType: attachedData.reportType,
                isLoadingReport: true,
                executedFunctionCall: TRIGGER_SEARCH,
              };

          resultMessages.push(mappedMessage);
          resultMessages.push(assistantMessage);
          return;
        }
        // For other types (phone, property, email, vehicle), just add the cleaned message
        // and continue with normal processing
      }
    }

    // If this is an assistant message without function calls that received metadata,
    // clear savedMetadata so it's only applied once
    if (
      mappedMessage &&
      mappedMessage.role === ROLES.ASSISTANT &&
      savedMetadata !== null
    ) {
      savedMetadata = null;
    }

    if (mappedMessage) {
      resultMessages.push(mappedMessage);
    }
  });

  return resultMessages;
};

export const mapConversationMessageToMessage = (
  message: ConversationMessage,
  metadata: string | null = null
): Message | null => {
  if (message.role === ROLES.ASSISTANT) {
    if (
      !message.function_call_outputs ||
      message.function_call_outputs.length === 0
    ) {
      // Use responses[0] if available, otherwise fall back to message
      const content = message.responses?.[0] || message.message || '';
      // Skip messages with empty content
      if (!content.trim()) {
        return null;
      }
      const result: Message = {
        content,
        role: ROLES.ASSISTANT,
        context_id: undefined,
      };

      // Apply metadata if available - restore all metadata values
      if (metadata) {
        try {
          const parsedMetadata = JSON.parse(metadata);

          // Restore basic fields
          if (parsedMetadata.permalink) {
            result.permalink = parsedMetadata.permalink;
          }
          if (parsedMetadata.searchType) {
            result.searchType = parsedMetadata.searchType;
          }
          if (parsedMetadata.context_id) {
            result.context_id = parsedMetadata.context_id;
          }
          if (parsedMetadata.response_id) {
            result.response_id = parsedMetadata.response_id;
          }

          // Restore params for person searches
          if (parsedMetadata.params) {
            result.params = parsedMetadata.params;
          }

          // Restore executedFunctionCall if present, or set to TRIGGER_SEARCH for person searches with params
          if (parsedMetadata.executedFunctionCall) {
            result.executedFunctionCall = parsedMetadata.executedFunctionCall;
          } else if (
            parsedMetadata.searchType === 'person' &&
            parsedMetadata.params
          ) {
            result.executedFunctionCall = TRIGGER_SEARCH;
          }

          // Mark that teaser needs to be loaded if hasTeaser is true
          if (
            parsedMetadata.hasTeaser &&
            parsedMetadata.searchType === 'person' &&
            parsedMetadata.params &&
            !result.teaser
          ) {
            result.isLoadingTeaser = true;
          }

          // Mark as loading report if we have permalink and searchType but no reports yet
          if (
            parsedMetadata.permalink &&
            parsedMetadata.searchType &&
            !result.reports
          ) {
            result.isLoadingReport = true;
          }
        } catch {
          // If metadata is not valid JSON, ignore it
        }
      }

      return result;
    }
    return null;
  } else if (message.role === ROLES.USER) {
    if (
      message.function_call_outputs &&
      message.function_call_outputs.length > 0
    ) {
      return null;
    }

    const messageContent = message.message.trim();

    // Check if this is a message generated from teaser selection
    // Format can be: "**User prompt:** {...}" or directly {...}
    let contentToCheck = messageContent;
    const userPromptMatch = messageContent.match(
      /^\*\*User prompt:\*\*\s*(.+)$/
    );
    if (userPromptMatch) {
      contentToCheck = userPromptMatch[1].trim();
    }

    // Try to parse as JSON to detect teaser selection messages
    try {
      const parsedContent = JSON.parse(contentToCheck);
      if (parsedContent.permalink && parsedContent.data) {
        const personName = parsedContent.data?.personName || 'this person';
        const friendlyMessage = `Tell me about ${personName}`;
        return {
          content: friendlyMessage,
          role: ROLES.USER,
          teaserSelectionMessage: friendlyMessage,
        };
      }
    } catch {
      // Not JSON, continue with normal processing
    }

    // Filter out internal permalink messages that don't have the teaser format
    // But allow messages with attached-data
    if (
      messageContent.includes('permalink') &&
      !messageContent.includes('data') &&
      !messageContent.includes('attached-data')
    ) {
      return null;
    }

    return {
      content: messageContent.replace('**User prompt:**', '').trim() || '',
      role: ROLES.USER,
    };
  }
  return null;
};
